import { zodResolver } from '@hookform/resolvers/zod';
import { FormValidationMessage } from '@repo/constants';
import { useUserList } from '@repo/lib';
import { TableTemplate, UserListItem } from '@repo/types';
import {
  Button,
  Checkbox,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Spinner,
} from '@ui/components';
import {
  CirclePlusIcon,
  CircleXIcon,
  SearchIcon,
  ShareIcon,
  XIcon,
} from 'lucide-react';
import { FC, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

export enum TemplateDialogVariant {
  SHARE = 'share',
  NEW = 'new',
}

interface TemplateDialogProps
  extends React.ComponentPropsWithoutRef<typeof Dialog> {
  variant: TemplateDialogVariant;
  onSubmit: (values: TemplateFormValues) => void;
  isSubmitting?: boolean;
  templates?: TableTemplate[];
}

const ShareTemplateSchema = z.object({
  email: z
    .string()
    .email({ message: FormValidationMessage.InvalidEmail })
    .min(1, { message: FormValidationMessage.Required }),
  userId: z.number().optional(),
  templateIds: z
    .array(z.number())
    .min(1, { message: 'Select at least one template' }),
});

const NewTemplateSchema = z.object({
  title: z
    .string()
    .min(1, { message: FormValidationMessage.Required })
    .max(21, FormValidationMessage.Max(21)),
});

export type TemplateFormValues =
  | z.infer<typeof ShareTemplateSchema>
  | z.infer<typeof NewTemplateSchema>;

export const TemplateDialog: FC<TemplateDialogProps> = (props) => {
  const {
    variant,
    onSubmit,
    isSubmitting = false,
    templates = [],
    ...restProps
  } = props;

  const {
    users,
    isLoading,
    selectedUser,
    handleSearchChange,
    handleSelectUser,
    handleClearSelection,
  } = useUserList();

  const [showResults, setShowResults] = useState(false);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const resultsContainerRef = useRef<HTMLDivElement>(null);

  const schema =
    variant === TemplateDialogVariant.SHARE
      ? ShareTemplateSchema
      : NewTemplateSchema;

  const form = useForm<TemplateFormValues>({
    resolver: zodResolver(schema),
    defaultValues:
      variant === TemplateDialogVariant.SHARE
        ? { email: '', userId: undefined, templateIds: [] }
        : { title: '' },
  });

  const emailValue = form.watch('email') as string;

  const handleUserClick = (user: UserListItem) => {
    handleSelectUser(user);
  };

  const handleTemplateCheckboxChange = (
    templateId: number,
    checked: boolean,
  ) => {
    const currentTemplateIds =
      (form.getValues('templateIds') as number[]) || [];

    if (checked) {
      form.setValue('templateIds', [...currentTemplateIds, templateId]);
    } else {
      form.setValue(
        'templateIds',
        currentTemplateIds.filter((id) => id !== templateId),
      );
    }
  };

  const handleSubmit = (values: TemplateFormValues) => {
    onSubmit(values);
  };

  useEffect(() => {
    if (variant === TemplateDialogVariant.SHARE) {
      handleSearchChange(emailValue || '');
    }
  }, [variant, emailValue, handleSearchChange]);

  useEffect(() => {
    if (selectedUser) {
      form.setValue('email', selectedUser.email);
      form.setValue('userId', selectedUser.id);
      setShowResults(false);
    }
  }, [selectedUser, form]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        resultsContainerRef.current &&
        !resultsContainerRef.current.contains(event.target as Node) &&
        emailInputRef.current &&
        !emailInputRef.current.contains(event.target as Node)
      ) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (restProps.open) {
      form.reset();
    }
  }, [restProps.open]);

  return (
    <Dialog {...restProps}>
      <DialogContent className="max-h-[90vh] min-w-fit overflow-hidden">
        <DialogHeader>
          <DialogTitle>
            {variant === TemplateDialogVariant.SHARE
              ? 'Share Template'
              : 'New Template'}
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            className="space-y-4"
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            {variant === TemplateDialogVariant.SHARE ? (
              <>
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex flex-col gap-2">
                        <FormLabel className="font-semibold">
                          Search user by email
                        </FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Input
                              placeholder="Search by email or enter email address"
                              {...field}
                              ref={emailInputRef}
                              onChange={(e) => {
                                field.onChange(e);
                                if (!e.target.value) {
                                  form.setValue('userId', undefined);
                                }
                              }}
                              onFocus={() => setShowResults(true)}
                            />
                            <div className="absolute right-3 top-1/2 flex -translate-y-1/2 items-center space-x-1">
                              {isLoading && <Spinner className="size-4" />}
                              {field.value && (
                                <button
                                  className="rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                                  type="button"
                                  onClick={() => {
                                    field.onChange('');
                                    form.setValue('userId', undefined);
                                    handleClearSelection();
                                  }}
                                >
                                  <XIcon className="size-4" />
                                </button>
                              )}
                              {!isLoading && !field.value && (
                                <SearchIcon className="size-4 text-gray-500" />
                              )}
                            </div>
                          </div>
                        </FormControl>
                        {showResults &&
                          field.value &&
                          field.value.length >= 2 && (
                            <div
                              ref={resultsContainerRef}
                              className="absolute z-10 mt-16 max-h-60 w-[calc(100%-3rem)] overflow-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg"
                            >
                              {users.length > 0 ? (
                                <ul className="divide-y divide-gray-100">
                                  {users.map((user: UserListItem) => (
                                    <li
                                      key={user.id}
                                      className="cursor-pointer px-4 py-2 hover:bg-gray-100"
                                      onClick={() => handleUserClick(user)}
                                    >
                                      <div className="font-medium">
                                        {user.email}
                                      </div>
                                      <div className="text-sm text-gray-500">
                                        {user.username}
                                      </div>
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <div className="px-4 py-2 text-sm text-gray-500">
                                  No users found
                                </div>
                              )}
                            </div>
                          )}
                        <FormMessage />
                      </div>
                    </FormItem>
                  )}
                />

                {/* Template selection section */}
                <div className="mt-8">
                  <FormField
                    control={form.control}
                    name="templateIds"
                    render={() => (
                      <FormItem>
                        <FormLabel className="mb-2 block font-semibold">
                          Select Templates to Share
                        </FormLabel>
                        <div className="max-h-60 space-y-2 overflow-y-auto rounded-md border p-2">
                          {templates.length > 0 ? (
                            templates.map((template) => (
                              <div
                                key={template.id}
                                className="flex items-center space-x-2 rounded p-2 hover:bg-gray-50"
                              >
                                <Checkbox
                                  id={`template-${template.id}`}
                                  onCheckedChange={(checked) =>
                                    handleTemplateCheckboxChange(
                                      template.id,
                                      !!checked,
                                    )
                                  }
                                />
                                <label
                                  className="flex-1 cursor-pointer text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                  htmlFor={`template-${template.id}`}
                                >
                                  {template.title}
                                </label>
                              </div>
                            ))
                          ) : (
                            <p className="p-2 text-sm text-gray-500">
                              No templates available
                            </p>
                          )}
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </>
            ) : (
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex flex-col gap-4">
                      <FormLabel className="w-20 font-semibold">
                        Title
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Enter template title"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </div>
                  </FormItem>
                )}
              />
            )}
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline">
                  <CircleXIcon className="mr-2 size-4" />
                  Cancel
                </Button>
              </DialogClose>
              <Button
                disabled={isSubmitting}
                type="submit"
              >
                {variant === TemplateDialogVariant.SHARE ? (
                  <>
                    <ShareIcon className="mr-2 size-4" />
                    Share
                  </>
                ) : (
                  <>
                    <CirclePlusIcon className="mr-2 size-4" />
                    Create
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
